import React, { useEffect, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import DateInput from '@/components/ui/date-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatTime } from '@/lib/utils';

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Alle 7 Tage (wöchentlich)' },
  { value: 'biweekly', label: 'Alle 14 Tage (14-tägig)' },
];

export default function CreateTurnusDialog({
  courseId,
  open,
  onOpenChange,
  onSuccess,
  testIdPrefix = 'turnus',
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    anchor_date: '',
    frequency: 'weekly',
    end_date: '',
    start_time: '',
  });
  const [hint, setHint] = useState(null);

  useEffect(() => {
    if (!open || !courseId) return;
    setLoading(true);
    api.get(`/attendance-lists/courses/${courseId}/schedule-suggestion`)
      .then((r) => {
        const data = r.data;
        setForm((f) => ({
          ...f,
          anchor_date: data.anchor_date || '',
          start_time: data.start_time && data.start_time !== '00:00' ? data.start_time : '',
          frequency: data.rhythm === 'biweekly' ? 'biweekly' : 'weekly',
        }));
        setHint(data);
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || 'Turnus-Vorschlag konnte nicht geladen werden');
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, courseId, onOpenChange]);

  const submit = async () => {
    if (!form.anchor_date) {
      toast.error('Bitte Ankerdatum wählen');
      return;
    }
    setSaving(true);
    try {
      const body = {
        anchor_date: form.anchor_date,
        frequency: form.frequency,
      };
      if (form.end_date) body.end_date = form.end_date;
      if (form.start_time) body.start_time = form.start_time;
      const r = await api.post(`/attendance-lists/courses/${courseId}/schedule`, body);
      toast.success(r.data.message || 'Turnus erstellt');
      onOpenChange(false);
      onSuccess?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Turnus konnte nicht erstellt werden');
    } finally {
      setSaving(false);
    }
  };

  const prepaidNote = hint?.prepaid_remaining != null
    ? `Prepaid: noch ${hint.prepaid_remaining} Einheiten — es werden höchstens so viele Termine ergänzt.`
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>Turnus erstellen</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-6 text-center text-sm text-slate-400">Laden…</div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-slate-600">
              Ab dem ersten vereinbarten Termin werden weitere Unterrichtstermine im gewählten Rhythmus angelegt.
            </p>
            {prepaidNote && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                {prepaidNote}
              </p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor={`${testIdPrefix}-anchor`}>Erster Termin (Anker)</Label>
              <DateInput
                id={`${testIdPrefix}-anchor`}
                value={form.anchor_date}
                onChange={(e) => setForm({ ...form, anchor_date: e.target.value })}
                data-testid={`${testIdPrefix}-anchor`}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Intervall</Label>
              <Select
                value={form.frequency}
                onValueChange={(v) => setForm({ ...form, frequency: v })}
              >
                <SelectTrigger data-testid={`${testIdPrefix}-frequency`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${testIdPrefix}-start-time`}>Unterrichtszeit (optional)</Label>
              <Input
                id={`${testIdPrefix}-start-time`}
                type="time"
                value={formatTime(form.start_time) || ''}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                data-testid={`${testIdPrefix}-start-time`}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${testIdPrefix}-end-date`}>Bis Datum (optional)</Label>
              <DateInput
                id={`${testIdPrefix}-end-date`}
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                data-testid={`${testIdPrefix}-end-date`}
              />
              <p className="text-[10px] text-slate-400">
                Leer lassen: bei Prepaid-Karten bis zur Rest-Einheit, sonst max. 2 Jahre.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Abbrechen
              </Button>
              <Button
                onClick={submit}
                disabled={saving}
                className="bg-brand-red hover:bg-brand-red/90"
                data-testid={`${testIdPrefix}-submit`}
              >
                {saving ? 'Erstelle…' : 'Turnus anlegen'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TurnusCreateButton({
  courseId,
  visible,
  onSuccess,
  className = '',
  testId = 'turnus-create-btn',
}) {
  const [open, setOpen] = useState(false);
  if (!visible || !courseId) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId}
        className={`px-1.5 py-0.5 rounded border border-brand-red/40 text-brand-red text-[9px] font-semibold hover:bg-brand-red/5 transition-colors ${className}`}
      >
        Turnus erstellen
      </button>
      <CreateTurnusDialog
        courseId={courseId}
        open={open}
        onOpenChange={setOpen}
        onSuccess={onSuccess}
        testIdPrefix={testId.replace('-btn', '')}
      />
    </>
  );
}
